import { Injectable, ViewContainerRef } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { LMContextService, LMLoggerService, LMSpecService, LMUrlUtilService } from '@app/services';
import { HttpParamEncoderService } from '@services/utils/http.param.encoder.service';
import {
  LMReport, LMReportSearchValues, LMReportSortColumn, LMReportsResponse
} from '../lm-reports';
import { KeyedResource, ListResource, PageResource } from '@app/resources';
import { AppConstants } from '@app/utils';
import { LMListSpecUtilService, LMPrintCsvInfo, LMPrintPdfInfo } from '@app/components';

@Injectable()
export class LMReportsCatalogService {
  private reportsSpec: LMReportsResponse;
  private activeTab: string;
  private allReportsSearchValues: LMReportSearchValues;
  private reportsSortColumn: LMReportSortColumn;

  constructor(
    private http: HttpClient,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmSpecService: LMSpecService,
    private lmListSpecUtilService: LMListSpecUtilService) {
  }

  public getActiveTab() {
    return this.activeTab;
  }

  public setActiveTab(activeTab: any) {
    this.activeTab = activeTab;
  }

  public getAllReportsSearchValues() {
    return this.allReportsSearchValues;
  }

  public setAllReportsSearchValues(allReportsSearchValues: any) {
    this.allReportsSearchValues = allReportsSearchValues;
  }

  public getStoredCatalogSpec() {
    return this.reportsSpec;
  }

  public setStoredCatalogSpec(spec: any) {
    this.reportsSpec = spec;
  }

  public getReportsCatalogData(postContent?: any, extraParams?: LMUrlParams): Observable<any> {
    postContent = postContent != null ? { ...postContent, ... { requestType: 'reportCatalog' } } : { requestType: 'reportCatalog' };
    let urlParams: LMUrlParams = { ...{output: 'spec', requestType: 'reportCatalog'}, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (extraParams) {
      urlParams = { ...urlParams, ...extraParams };
    }
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
    return this.makeHttpCall(url, postContent, extraParams);
  }

  public buildReportUrl(report:LMReport): string {
    let urlParams: LMUrlParams = { ...{output: 'spec' }, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (report.owner != '') {
      urlParams = { ...urlParams, ...{userfolder: report.id } };
    }
    return LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), report.type.toLowerCase(), report.resourceId, null, urlParams);
  }

  public getListSpec(resourceId: string): Observable<any> {
    const urlParams: LMUrlParams = this.lmContextService.getContext().contextSessionKeys;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'list', resourceId, null, urlParams);
    return this.lmSpecService.getListSpec(url);
  }

  public buildCatalogNavigationUrl(extraParams?: LMUrlParams) {
    // Pass in { panel: 'favorites' | 'all' | 'printfiles' }
    let urlParams: LMUrlParams = { ...{requestType: 'reportCatalog'}, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    if (extraParams) {
      urlParams = { ...urlParams, ...extraParams };
    }
    return LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
  }

  public deleteUserFolderReport(reportId: string): Observable<any> {
    const postContent = { /*action: '_execute',*/ requestType: 'deleteUserFolderReport', reportId };
    const urlParams: LMUrlParams = { ...{requestType: 'deleteUserFolderReport', reportId}, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'report', 'catalog', null, urlParams);
    return this.makeHttpCall(url, postContent, null);
  }

  public getReportDefinitionRecordDataView(userFolderId: string): Observable<any> {
    const searchParams = {
      sortOrderName: 'ReportDefinition.ByReportName',
      pageop: 'load',
      isAscending: true,
      pagesize: 1
    };
    const urlParams: LMUrlParams = { ...searchParams, ...this.lmContextService.getContext().contextSessionKeys } as LMUrlParams;
    const url = LMUrlUtilService.buildUrl2(this.lmUrlUtilService.getBaseUrl(), 'list', 'ReportDefinition.ReportCatalogDataList', null, urlParams);
    const requestParams = {
      'fc.ReportDefinition_prd_UserFolder': 'contains(' + userFolderId + ')'
    };
    return this.makeHttpCall(url, null, requestParams);
  }

  private makeHttpCall(url: string, postContent?: any, requestParams?: any): Observable<any> {
    let params = new HttpParams({ encoder: new HttpParamEncoderService() });
    if (requestParams) {
      Object.keys(requestParams).forEach((key: string) => params = params.set(key, requestParams[key]));
    }
    if (postContent) {
      return <Observable<any>> this.http.post(url, postContent, { params }).pipe(catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
    } else {
      return <Observable<any>> this.http.get(url, { params }).pipe(catchError(error => {
        this.lmLoggerService.error(error);
        return throwError(error || 'Server error');
      }));
    }
  }

  public buildListCsvSettings(report: LMReport, spec: LMListSpec, listUrlInfo: LMListUrlInfo, modalPlaceholder: ViewContainerRef) {
    const actionReference = {
      automationId: "StandardExportToCSV",
      id: "StandardExportToCSV",
      label: "Export To CSV",
      type: "standard"
    };
    const listResourceImpl = new ListResource({ resourceId: spec.resourceId });
    const settings: LMPrintCsvInfo = <LMPrintCsvInfo>{
      modalPlaceholder,
      resource: listResourceImpl,
      fromResource: this.buildListFromResource(listUrlInfo),
      fromList: listUrlInfo.fromListResource,
      actionReference,
      csvType: this.getListCSVType(spec.busListSpec),
      name: report.title,
      dependentPanels: null,
      cubeOverrides: undefined,
      cubeOptions: undefined,
      userFolder: spec.userFolder,
      selectedRows: [],
      filterFields: [],
      scheduleActionAllowed: spec.scheduleActionAllowed,
      includeRecordLimit: true,
      isTreeList:  spec.busListSpec.implementsTreeView,
      hasDetailSections: this.lmListSpecUtilService.hasDetailSections(spec),
      singleRecordSelected: false,
      dataViewSet: undefined,
      selectRequestData: undefined,
      selectedDetailPanelIndex: undefined,
    };
    return settings;
  }

  private buildListFromResource(listUrlInfo: LMListUrlInfo): KeyedResource {
    let fromResource: KeyedResource;

    if (listUrlInfo.sourceResource) {
      fromResource = new KeyedResource({
        resourceId: listUrlInfo.sourceResource.resourceId,
        resourceType: listUrlInfo.sourceResource.resourceType
      });
    }
    return fromResource;
  }

  private getListCSVType(busListSpec: LMBusListSpec): string {
    if (busListSpec.implementsTreeView) {
      return (busListSpec.detailSections) ?
        AppConstants.TREE_LIST_WITH_DETAILS_CSV : AppConstants.TREE_LIST_CSV;
    }
    return AppConstants.REGULAR_LIST_CSV;
  }

  public buildListPdfSettings(report: LMReport, spec: LMListSpec, listUrlInfo: LMListUrlInfo) {
    const actionReference = {
      automationId: "StandardPrintToFile",
      id: "StandardPrintToFile",
      label: "Print To PDF",
      type: "standard"
    };
    const pdfType = this.getListPDFType(spec.busListSpec);
    const listResourceImpl = new ListResource({ resourceId: spec.resourceId });
    const settings = <LMPrintPdfInfo>{
      resource: listResourceImpl,
      fromResource: this.buildListFromResource(listUrlInfo),
      fromList: listUrlInfo.fromListResource,
      actionReference,
      name: report.title,
      pdfType,
      includeSearchForm: false,
      cubeOverrides: undefined,
      cubeOptions: undefined,
      userFolder: spec.userFolder,
      fontOffset: spec.pdfFontOffset || spec.busListSpec.fontOffset,
      orientation: spec.pdfOrientation || spec.busListSpec.orientation,
      dataViewSet: undefined,
      selectedRows: undefined,
      filterFields: undefined,
      scheduleActionAllowed: spec.scheduleActionAllowed,
      includeRecordLimit: true,
      selectRequestData: undefined,
    };
    return settings;
  }
  public getListPDFType(busListSpec: LMBusListSpec): string {
    if (busListSpec.implementsTreeView) {
      return (busListSpec.detailSections) ?
        AppConstants.TREE_LIST_WITH_DETAILS_PDF : AppConstants.TREE_LIST_PDF;
    }
    if (busListSpec.detailSections || busListSpec.showChangedValuesOnRight) {
      return AppConstants.LIST_WITH_DETAILS_PDF;
    }
    return AppConstants.REGULAR_LIST_PDF;
  }

  public getReportsSortColumn() {
    return this.reportsSortColumn;
  }
  public setReportsSortColumn(columnId: string, ascending: boolean) {
    this.reportsSortColumn = {
      columnId,
      ascending
    };
  }
}
